/**
 * Who may see a sick report (fix S2, 2026-09-29). FRAMEWORK T4 (managers see actions, never symptoms), VP2 (the worker's
 * privacy is theirs) and IP2 (support, not punishment).
 *
 * An "I'm sick or was exposed" report is readable only by the person who sent it and by whoever must act on it: the
 * person in charge (a staff member flagged in charge, a shift lead) or a manager, admin or owner of the location
 * (Food Code 2-201.11: the employee reports to the person in charge; 2-201.12: the person in charge excludes or
 * restricts). A coworker never sees that it exists: on a shift of three, "someone is sick, 20 minutes ago" names them.
 *
 * The API applies these to every list, feed and count a coworker can read (GET /incidents, GET /today, GET /routine,
 * the live-sync shape); the web and phone demos apply the same functions (T7).
 */
import { PIC_CERT_TITLE } from "./ruleshapes.ts";
import { isPrivateType } from "./incidents.ts";
import type { Id, IncidentType, Role } from "./model.ts";
import { SICK_REPORT_BLOCK_TITLE, type Finding, type FindingKind } from "./schedule.ts";

/** The signed-in person reading a list. `isPersonInCharge`: their employee record is flagged in charge. */
export interface ReportViewer { userId: Id; role: Role; isPersonInCharge?: boolean; employeeId?: Id }

/** Managers, admins, owners and the person in charge act on sick reports, so they see them. */
export function seesPrivateReports(v: Pick<ReportViewer, "role" | "isPersonInCharge">): boolean {
  // Setting a place up for its owner (core setuprole.ts) is not acting on its team: a setup member never sees a sick report.
  if (v.role === "setup") return false;
  return v.role !== "staff" || v.isPersonInCharge === true;
}

/** May this viewer see this report? Every report but a sick one: yes. A sick one: its sender and those who act on it. */
export function canSeeReport(v: ReportViewer, i: { type?: IncidentType | string; reportedBy?: Id }): boolean {
  if (!i.type || !isPrivateType(i.type as IncidentType)) return true;
  return i.reportedBy === v.userId || seesPrivateReports(v);
}

/** The reports this viewer may see, in the same order. */
export function visibleReports<T extends { type?: IncidentType | string; reportedBy?: Id }>(v: ReportViewer, list: readonly T[]): T[] {
  return list.filter((i) => canSeeReport(v, i));
}

type Block = { kind: FindingKind; title: string; detail: string; requirementIds: string[] };

/** A coworker's status as another coworker sees it: whether they're cleared for food work, never why. */
export const NOT_CLEARED_FOR_COWORKER: Block = { kind: "not_checked_in", title: "not cleared for food work yet", detail: "The person in charge has the details.", requirementIds: [] };

/**
 * Someone else's shift gate for a viewer who doesn't act on health: "hasn't checked in", "no food work today" and "shouldn't
 * do food work today" all read the same ("not cleared for food work yet"), so a sick coworker can't be told apart from one
 * who hasn't checked in yet. Lessons and permit blocks are unchanged (not health).
 */
export function gateForCoworker<G extends { ready: boolean; blocking: Block[] }>(gate: G): G {
  const health = gate.blocking.filter((b) => b.kind === "not_checked_in");
  if (!health.length) return gate;
  return { ...gate, blocking: [NOT_CLEARED_FOR_COWORKER, ...gate.blocking.filter((b) => b.kind !== "not_checked_in")] };
}

/**
 * Today's findings for this viewer: a sick report they can't see is dropped, and another person's check-in or sick-report
 * block reads "not cleared for food work yet" (as gateForCoworker). `incidents` gives each report's type and sender.
 */
export function findingsForViewer(v: ReportViewer, all: readonly Finding[], incidents: readonly { id: Id; type?: IncidentType | string; reportedBy?: Id }[]): Finding[] {
  if (seesPrivateReports(v)) return [...all];
  const byId = new Map(incidents.map((i) => [i.id, i]));
  const out: Finding[] = [];
  for (const f of all) {
    // The person in charge's certificate note is for managers (ruleshapes.ts picCertNote): a coworker never sees it.
    if (f.kind === "credential" && f.title === PIC_CERT_TITLE) continue;
    if (f.kind === "incident" && f.ref) {
      const i = byId.get(f.ref);
      if (i && !canSeeReport(v, i)) continue;
    }
    if (f.kind === "not_checked_in" && f.ref && f.ref !== v.employeeId) {
      const name = f.title.includes(": ") ? f.title.slice(0, f.title.indexOf(": ")) : "";
      out.push({ ...f, title: name ? `${name}: ${NOT_CLEARED_FOR_COWORKER.title}` : NOT_CLEARED_FOR_COWORKER.title, detail: NOT_CLEARED_FOR_COWORKER.detail, requirementIds: [] });
      continue;
    }
    out.push(f);
  }
  return out;
}

/** True when a gate line is about a sick report (used by tests and screens that must never show it to a coworker). */
export const isSickReportBlock = (b: { title: string }) => b.title === SICK_REPORT_BLOCK_TITLE;
